import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ScrollableX } from "../../styles/shared/card";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { blurAnimation } from "../../styles/shared/loading";
import LoadingSpinner from "./loading-spinner";
import { TextContainer } from "../../styles/shared/table";
import TextInput from "./text-input";
import { ICON_ID } from "../icons/google-icon";
import { FetchMoreData } from "./table-view";

export interface CardTableViewConfig {
  render: (row: { [key: string]: any }) => React.ReactNode;
}

export interface CardTableRowData {
  id: string | number;
  [key: string]: any;
}

interface CardTableViewProps {
  id: string;
  cardConfig: CardTableViewConfig;
  toolbar?: () => React.ReactNode;
  fetchMoreData?: FetchMoreData
  data: CardTableRowData[];
  totalCount: number;
  preLoadedData?: boolean;
  emptyText: string;
  onRowClick?: (row: { [key: string]: any }) => void;
  headerText?: string;
  search: boolean
}

export default function CardTableView({
  id,
  cardConfig,
  toolbar,
  fetchMoreData,
  data,
  totalCount,
  emptyText,
  preLoadedData = false,
  headerText,
  onRowClick,
  search
}: CardTableViewProps) {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);
  const [displayData, setDisplayData] = useState(data);
  const [searchText, setSearchText] = useState<string>("");

  const runFetch = async (params: { searchText?: string; reset?: boolean }) => {
    if (!fetchMoreData) {
      return;
    }

    setLoading(true);
    await fetchMoreData(params).finally(() => setLoading(false));
  };

  useInfiniteScroll({
    dataLength: displayData.length,
    totalCount: totalCount,
    fetchData: runFetch,
    loading,
    loadingRowRef
  });

  useEffect(() => {
    setDisplayData(data);
  }, [data]);

  useEffect(() => {
    if (data.length === 0 && !preLoadedData && fetchMoreData && !searchText && !loading) {
      setPageLoading(true);
      fetchMoreData?.({
        searchText: "",
        reset: false
      }).finally(() => setPageLoading(false));
    }
  }, []);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchData = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (!fetchMoreData) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      runFetch({
        searchText: value,
        reset: true
      });
    }, 350);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);


  if (pageLoading) {
    return (
      <ScrollableX>
        <NoContent>
          <LoadingSpinner></LoadingSpinner>
        </NoContent>
      </ScrollableX>
    );
  }

  return (
    <>
      {(search || toolbar) && (
        <Toolbar>
          {search && (
            <SearchWrapper>
              <TextInput id="txtSearch" autoBlurAfterChange label="Search" value={searchText ?? ""} onChange={(event) => searchData(event)} type="text" iconID={ICON_ID.Search} ></TextInput>
            </SearchWrapper>
          )}
          {toolbar && (
            <div>{toolbar()}</div>
          )}
          {loading && (
            <LoadingSpinner></LoadingSpinner>
          )}

        </Toolbar>
      )}
      <TableWrapper id={id} $toolbar={setSearchText != null}>

        {headerText && (
          <TableHeader>
            <ScrollableX>
              <HeaderRow>
                <TableCell>
                  {headerText}
                </TableCell>

              </HeaderRow>
            </ScrollableX>
          </TableHeader>

        )}
        <ScrollableX>
          {(displayData.length === 0 && !loading) ? (
            <NoContent>
              <TextContainer>{emptyText}</TextContainer>
            </NoContent>
          ) : (
            <TableBody>
              {displayData.map((row) => (
                <>
                  <TableRow
                    onClick={() => (onRowClick ? onRowClick(row) : null)}
                    ref={rowRef}
                    key={`dataRow-${row.id}`}
                  >
                    <TableCell>
                      {cardConfig.render(row)}
                    </TableCell>
                  </TableRow>
                </>
              ))}

              {totalCount !== displayData.length && (
                <LoadingRow ref={loadingRowRef}>
                  <LoadingCell ></LoadingCell>
                  <LoadingCell></LoadingCell>
                </LoadingRow>
              )}
            </TableBody>
          )}
        </ScrollableX>
      </TableWrapper >
    </>
  );
}

interface TableWrapperProps {
  $toolbar: boolean;
}


const TableWrapper = styled.div<TableWrapperProps>`
   box-shadow: ${(props) => props.theme.application.boxShadow};
`;

const TableBody = styled.div`
  width:100%;
`;

const TableHeader = styled.div`
  position: sticky;
  padding:10px;
  top: 0;
  background: ${(props) => props.theme.application.scheme.primary};
  z-index: 2;
  color: ${(props) => props.theme.application.scheme.contrast};
`;

const SearchWrapper = styled.div`  
min-width: 30%;

`

const Toolbar = styled.div`  
display:flex;
width:100%;
flex-direction:row;
background:rgba(255, 255, 255, 0.5);
padding-left:5px; 
align-items: center;
gap:10px;
overflox-x:
`

const HeaderRow = styled.div`
      display: flex;
    align-items: center;
  height:100%;
  padding-left:10px;
  font-size:1.5rem;

`;

const TableRow = styled.div`
  border-bottom:${(props) => props.theme.application.table.border};
`;

const TableCell = styled.div`
  background: ${(props) => props.theme.application.table.cellBackground};
  display: flex;
  flex-direction:column;

`;

const LoadingRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  padding: 10px 0;
  animation: ${blurAnimation} 0.5s infinite;
  height: 50px;  
`;

const LoadingCell = styled.div`
  padding: 20px;
  border-radius: 15px;
  padding: 20px;
  border-radius: 6px;
  background: ${(props) => props.theme.application.placeholder};
  margin: 0px 16px;
`;

const NoContent = styled.div`
  text-align: center;
  margin-top: 10%;
  margin-bottom: 10%;
`;
